import {useEffect, useState, useReducer, useRef} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";

import TIcon from "../../components/ui/TIcon";
import FieldInput from "../../components/form/FieldInput";
import AuthShell from "../../components/ui/AuthShell";
import * as _ from 'lodash';
import {regexPhoneNumber} from "../../helpers/utils";
import * as SBService from "../../helpers/sportbookService"
import { apiErrorMessage } from "../../helpers/apiError";
import { registerPayload } from "../../helpers/registerPayload";
import { luuPhienDangNhap } from "../../helpers/authSession";
import { useGoogleSignIn } from "../../hooks/googleSignIn";
import {trackEvent} from "../../helpers/analytics";

import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";


import Resizer from "react-image-file-resizer";
import {useRecoilState} from "recoil";
import {loaderState} from "../../states/common";
import toast from "react-hot-toast";

/** Hồ sơ Google mà màn đăng nhập chuyển sang qua location.state. */
interface HoSoGoogleState {
    idToken: string;
    email: string;
    name: string | null;
    picture: string | null;
}

/**
 * Màn đăng ký, dùng cho hai luồng.
 *
 *  - Số điện thoại: tên, email (BẮT BUỘC - khôi phục mật khẩu gửi OTP qua
 *    email), số điện thoại, mật khẩu → ảnh đại diện.
 *  - Google: người dùng đã xác thực bên Google nhưng Google không cho biết số
 *    điện thoại, mà số điện thoại chính là tên đăng nhập của hệ thống. Nên vẫn
 *    phải đi qua đúng luồng này, chỉ khác: KHÔNG có ô mật khẩu (tài khoản
 *    Google không có mật khẩu), email khoá lại vì là của Google, tên điền sẵn
 *    theo Google và sửa được.
 */
const RegisterPage = () => {
    const [loader, setLoader] = useRecoilState(loaderState)
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const navigate = useNavigate()
    const location = useLocation()

    /* Hồ sơ Google: lấy từ màn đăng nhập qua location.state, hoặc do người dùng
       bấm nút Google ngay tại màn này. Giữ trong state chứ không phải ref vì nó
       quyết định bố cục của form (có ô mật khẩu hay không), nên đặt vào là phải
       vẽ lại. Tải lại trang thì mất - đúng, lúc đó không còn ID token nên phải
       bấm Google lại từ đầu. */
    const [hoSoGoogle, setHoSoGoogle] = useState<HoSoGoogleState | null>(
        ((location.state as any)?.google as HoSoGoogleState) ?? null
    )
    const laGoogle = !!hoSoGoogle?.idToken

    /*
     * Các bước, đặt TÊN chứ không đánh số.
     *
     * Hai luồng có số bước khác nhau nên "bước 2" là hai màn khác hẳn nhau tuỳ
     * luồng - viết theo số thì mỗi lần đọc lại phải tự nhẩm xem đang ở đâu, và
     * đã có một lần nhầm thật (nút XÁC NHẬN ở luồng Google rơi vào nhánh xử lý
     * OTP).
     *
     * Luồng Google không có bước OTP: email đã được Google xác thực rồi, bắt
     * xác nhận lại chính email đó là bắt người dùng làm thừa.
     */
    const cacBuoc: Array<'thongTin' | 'otp' | 'anh'> = laGoogle
        ? ['thongTin', 'anh']
        : ['thongTin', 'otp', 'anh']
    const maxStep = cacBuoc.length
    const [step, setStep] = useState(1)
    const buoc = cacBuoc[step - 1]

    /* Đếm ngược tới lúc được xin mã mới. Máy chủ chặn 60 giây giữa hai lần xin,
       nên nút "Gửi lại mã" phải khoá đúng bấy nhiêu - bấm sớm chỉ nhận 429. */
    const [choGuiLai, setChoGuiLai] = useState(0)
    const [fileAvatar, setFileAvatar] = useState('')
    const [base64FileUrl, setBase64FileUrl] = useState<string | null>('')
    const [errorMessage, setErrorMessage] = useState('')
    const [formData, setFormData] = useState({
        name: hoSoGoogle?.name ?? '',
        username: '',
        profilePicture: '',
        phone: '',
        password: '',
        email: hoSoGoogle?.email ?? '',
        confirm_password: '',
        avatar: '',
        otp: ''
    })

    let [formError, setFormError] = useState({
        name: '',
        username: '',
        phone: '',
        password: '',
        email: '',
        confirm_password: '',
        avatar: '',
        otp: ''
    })

    const cropperRef = useRef<ReactCropperElement>(null);


    useEffect(() => {
        document.title = "SB - Register"
    }, []);

    useEffect(() => {
        if (choGuiLai <= 0) {
            return
        }
        const dem = setTimeout(() => setChoGuiLai((n) => n - 1), 1000)

        return () => clearTimeout(dem)
    }, [choGuiLai]);

    const changeInput = (label: any, event: any) => {
        _.update(formData, label, () => event.target.value)
        setFormData(formData)
        _.update(formError, label, () => '')
        forceUpdate()
    }

    /** Màn đang hiển thị một ảnh đại diện nào đó - của Google, hoặc vừa chọn. */
    const dangCoAnh = () => !!base64FileUrl || (laGoogle && !!hoSoGoogle?.picture)

    /** Ảnh người dùng vừa cắt ở bước 2, dạng base64 trần. Không chọn thì null. */
    const anhDaCat = (): string | null => {
        if (!base64FileUrl) {
            return null
        }
        const cropper: any = cropperRef.current?.cropper
        if (!cropper) {
            return null
        }
        const imageData = cropper.getCroppedCanvas().toDataURL()

        return imageData.substr(imageData.toString().indexOf(',') + 1)
    }

    /**
     * Gửi đăng ký bằng số điện thoại.
     *
     * Thân request do registerPayload dựng, không gửi cả cục formData: form giữ
     * nhiều trường hơn API nhận (confirm_password, username, avatar, otp...) và
     * có khoá phải BỎ HẲN thay vì gửi rỗng - luật nằm ở helpers/registerPayload.ts
     * kèm test, vì đây là chỗ đã sai một lần và sai im lặng.
     */
    const guiDangKySdt = async () => {
        formData.phone = formData.username
        setFormData(formData)
        const submitFormData = registerPayload(formData, anhDaCat())

        // KHÔNG console.log payload: nó chứa mật khẩu người dùng vừa gõ và cả
        // ảnh base64, mà console của trình duyệt thì ai mở DevTools cũng đọc được.
        await SBService.register(submitFormData)
        trackEvent('sign_up', {method: 'phone'})
        toast('Đăng ký thành công, vui lòng đăng nhập để truy cập hệ thống')
        navigate('/auth/login?success=true')
    }

    /**
     * Hoàn tất đăng ký bằng Google.
     *
     * Không gửi email: máy chủ tự đọc email từ ID token đã được Google ký, nên
     * cái gì gửi từ đây cũng không được tin. Không gửi ảnh nếu người dùng không
     * chọn - khi đó máy chủ lấy ảnh đại diện Google.
     */
    const guiDangKyGoogle = async () => {
        const anh = anhDaCat()
        const than = await SBService.googleRegister({
            idToken: hoSoGoogle!.idToken,
            phone: formData.username,
            name: formData.name.trim(),
            ...(anh ? {profilePicture: anh} : {}),
        })

        if (!luuPhienDangNhap(than)) {
            // Máy chủ trả 2xx nhưng không có token: đưa về màn đăng nhập còn hơn
            // vào trang trong rồi gặp 401 ở mọi lời gọi tiếp theo.
            toast('Đăng ký thành công, vui lòng đăng nhập để truy cập hệ thống')
            navigate('/auth/login?success=true')

            return
        }

        trackEvent('sign_up', {method: 'google'})
        toast('Đăng ký thành công')
        navigate('/')
    }

    /*
     * Nút "Tiếp tục với Google" ngay trên màn đăng ký.
     *
     * Email chưa có tài khoản thì KHÔNG rời màn này: chỉ đặt hồ sơ Google vào
     * state, form tự đổi thành luồng Google (điền sẵn tên và email, bỏ ô mật
     * khẩu) và người dùng chỉ còn thiếu số điện thoại. Email đã có tài khoản thì
     * đó là đăng nhập, vào thẳng trang chủ.
     */
    const {bamGoogle} = useGoogleSignIn({
        setChoDoi: setLoader,
        onLoi: setErrorMessage,
        onCanDangKy: (hoSo) => {
            setHoSoGoogle(hoSo)
            setFormData({
                ...formData,
                name: formData.name || hoSo.name || '',
                email: hoSo.email,
                password: '',
                confirm_password: '',
            })
            setFormError(initLoi())
            setStep(1)
        },
        onDangNhap: (than) => {
            if (!luuPhienDangNhap(than)) {
                setErrorMessage('Đăng nhập không thành công, vui lòng thử lại')

                return
            }
            trackEvent('login', {method: 'google'})
            navigate('/')
        },
    })

    /** Một bộ lỗi rỗng - dùng mỗi khi đặt lại toàn bộ lỗi của form. */
    const initLoi = () => ({
        name: '', username: '', phone: '', password: '', email: '',
        confirm_password: '', avatar: '', otp: '',
    })

    const nextStep = async (e: any) => {
        e.preventDefault()
        setErrorMessage('')
        if (!checkValidStep()) {
            return
        }

        if (buoc === 'thongTin') {
            /*
             * Hỏi máy chủ xem số điện thoại (và email) đã có ai dùng chưa, NGAY
             * TẠI ĐÂY.
             *
             * Tài khoản chỉ được tạo ở bước cuối, nên không hỏi trước thì người
             * dùng gõ trùng số, đi hết mấy màn, rồi mới bị báo trùng ở một chỗ
             * không có ô số điện thoại để sửa - đúng cái đã xảy ra trên UAT.
             *
             * Email của luồng Google không hỏi: nó là email Google đã xác thực,
             * và nếu đã có tài khoản thì bước đăng nhập Google trước đó đã cho
             * vào thẳng rồi, không rơi tới màn này.
             */
            setLoader(true)
            try {
                await SBService.checkRegisterAvailability({
                    phone: formData.username,
                    ...(laGoogle ? {} : {email: formData.email.trim()}),
                })

                /* Luồng số điện thoại: xin mã xác nhận luôn, để màn sau có cái
                   mà gõ. Gộp vào cùng một lần bấm chứ không thêm một nút "gửi
                   mã" nữa - người dùng vừa khai email xong, không có lý do gì
                   bắt họ xác nhận ý định thêm lần nữa. */
                if (!laGoogle) {
                    await SBService.requestRegisterOtp({
                        phone: formData.username,
                        email: formData.email.trim(),
                    })
                    setChoGuiLai(60)
                }
                setLoader(false)
            } catch (e: any) {
                setLoader(false)
                // Gắn lỗi vào đúng ô để người dùng biết phải sửa cái nào; lỗi
                // nào không phải chuyện trùng thì hiện chung ở cuối form.
                const theoTruong = e?.response?.data?.errors ?? {}
                if (theoTruong.phone || theoTruong.email) {
                    setFormError({
                        ...initLoi(),
                        username: theoTruong.phone ? 'Số điện thoại này đã được đăng ký' : '',
                        email: theoTruong.email ? 'Email này đã được đăng ký' : '',
                    })
                } else {
                    setErrorMessage(apiErrorMessage(e))
                }

                return
            }

            setStep(step + 1)

            return
        }

        if (buoc === 'otp') {
            setLoader(true)
            try {
                await SBService.verifyRegisterOtp({
                    email: formData.email.trim(),
                    otp: formData.otp.trim(),
                })
                setLoader(false)
            } catch (e: any) {
                setLoader(false)
                // Mã sai hay hết hạn đều là invalidOTP; gắn vào ô mã chứ không
                // để chung cuối form, vì đó đúng là ô cần sửa.
                setFormError({...initLoi(), otp: apiErrorMessage(e)})

                return
            }

            setStep(step + 1)

            return
        }

        /*
         * Bước ảnh đại diện là bước gửi đăng ký, cho cả hai luồng.
         *
         * Gửi ở đây chứ không sớm hơn vì ảnh đi kèm ngay trong thân request:
         * tách ra thành hai lời gọi thì có tài khoản tạo xong mà thiếu ảnh nếu
         * lời gọi thứ hai hỏng.
         */
        setLoader(true)
        try {
            if (laGoogle) {
                await guiDangKyGoogle()
            } else {
                await guiDangKySdt()
            }
            setLoader(false)
        } catch (e: any) {
            // Đọc `error` rồi dừng là chỗ đã làm người dùng nhìn thấy đúng chữ
            // "unprocessableEntity": đó là tên chung của mã 422, còn lý do thật
            // nằm trong `errors` ngay bên cạnh. apiErrorMessage xếp thứ tự giúp.
            setErrorMessage(apiErrorMessage(e))
            setLoader(false)
        }
    }

    /** Xin lại mã. Máy chủ chặn 60 giây nên nút chỉ mở khi hết đếm ngược. */
    const guiLaiMa = async () => {
        setErrorMessage('')
        setFormError(initLoi())
        setLoader(true)
        try {
            await SBService.requestRegisterOtp({
                phone: formData.username,
                email: formData.email.trim(),
            })
            setChoGuiLai(60)
            toast('Đã gửi lại mã xác nhận')
        } catch (e: any) {
            setErrorMessage(apiErrorMessage(e))
        } finally {
            setLoader(false)
        }
    }

    const prevStep = (e: any) => {
        e.preventDefault()
        if (step > 1) {
            setStep(step - 1)
        } else {
            navigate(-1)
        }
    }

    const checkValidStep = () => {
        let isValid = true;
        const initFormError = initLoi()

        if (buoc === 'otp') {
            const ma = formData.otp.trim()
            if (!ma) {
                initFormError.otp = 'Bắt buộc'
                isValid = false
            } else if (!/^\d{6}$/.test(ma)) {
                initFormError.otp = 'Mã gồm 6 chữ số'
                isValid = false
            }
            setFormError(initFormError)

            return isValid
        }

        if (buoc === 'thongTin') {
            if (!formData.name) {
                initFormError.name = 'Bắt buộc'
                isValid = false
            }

            /* Email bắt buộc: khôi phục mật khẩu gửi OTP qua email, nên tài khoản
               không có email là tài khoản mất mật khẩu thì mất luôn. Luồng Google
               không kiểm tra vì email là của Google, ô nhập đã khoá. */
            if (!laGoogle) {
                const email = formData.email.trim()
                if (!email) {
                    initFormError.email = 'Bắt buộc'
                    isValid = false
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    initFormError.email = 'Định dạng email không đúng'
                    isValid = false
                }
            }

            // Tài khoản Google không có mật khẩu - đăng nhập bằng chính nút Google.
            if (!laGoogle) {
                if (!formData.password) {
                    initFormError.password = 'Bắt buộc'
                    isValid = false
                } else if (formData.password.length < 6) {
                    initFormError.password = 'Mật khẩu không được ít hơn 6 ký tự'
                    isValid = false
                }

                if (!formData.confirm_password) {
                    initFormError.confirm_password = 'Bắt buộc'
                    isValid = false
                } else if (formData.confirm_password !== formData.password) {
                    initFormError.confirm_password = 'Không khớp với Mật khẩu đã nhập'
                    isValid = false
                }
            }

            if (!formData.username) {
                initFormError.username = 'Bắt buộc'
                isValid = false
            } else {
                if (!regexPhoneNumber(formData.username)) {
                    initFormError.username = 'Định dạng số điện thoại không đúng'
                    isValid = false
                }
            }
        }
        setFormError(initFormError)

        return isValid;
    }

    const getBase64FromFile = async (file: any) => {
        return new Promise(resolve => {
            Resizer.imageFileResizer(
                file,
                600,
                600,
                "JPEG",
                100,
                0,
                (uri) => {
                    resolve(uri);
                },
                "base64"
            );
        });
    };

    const handleFileInputChange = async (e: any) => {
        if (e.target.files[0]) {
            setFileAvatar(e.target.files[0]);
            let result:any = await getBase64FromFile(e.target.files[0])
            setBase64FileUrl(result)
        }
    };



    useEffect(() => {

    }, [fileAvatar])


    const stepTitle =
        buoc === 'thongTin' ? 'ĐĂNG KÝ' : buoc === 'otp' ? 'XÁC NHẬN EMAIL' : 'ẢNH ĐẠI DIỆN';

    return <AuthShell
        showTabs={buoc === 'thongTin' && !laGoogle}
        /* Từ bước 2 trở đi màn này đã có nút mũi tên quay lại của riêng nó, và
           nút đó lùi một bước. Để AuthShell vẽ thêm nút "Quay lại" nữa thì trên
           cùng một màn có hai nút giống hệt nhau mà một cái rời hẳn trang về
           đăng nhập - mất sạch những gì vừa gõ, với luồng Google là mất cả ID
           token nên phải bấm đăng nhập Google lại từ đầu. */
        showBack={buoc === 'thongTin'}
        subtitle={buoc === 'thongTin'
            ? (laGoogle
                ? 'Còn một bước nữa: số điện thoại cũng là tên đăng nhập của bạn.'
                : 'Tạo tài khoản để lưu hạng cơ, theo dõi lịch sử thi đấu và đăng ký giải.')
            : undefined}
        footer={<p className="auth-foot">
            Đã có tài khoản? <Link to="/auth/login">Đăng nhập</Link>
        </p>}
    >
        {step > 1 ? <div className="page-head" style={{padding: '18px 8px 0'}}>
            <h1>{stepTitle}</h1>
            <button type="button" className="icon-btn" aria-label="Quay lại"
                    onClick={(e) => prevStep(e)}>
                <TIcon name="arrow-left"/>
            </button>
        </div> : <></>}

        {/* Chỉ báo bước — 2 bước với Google, 3 bước với số điện thoại */}
        <div style={{display: 'flex', gap: 6, padding: '18px 8px 0'}}>
            {Array.from({length: maxStep}, (_x, i) => i + 1).map((n) => <span key={n} style={{
                height: 3, flex: 1, borderRadius: 999,
                background: n <= step ? 'var(--biadi-brick)' : 'var(--biadi-line)',
            }}/>)}
        </div>

        <form className="auth-form" onSubmit={(e) => nextStep(e)}>
            {buoc === 'thongTin' ?
                <>
                    {laGoogle ? <p style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        color: 'var(--biadi-steel)', font: '500 13px var(--font-ui)', padding: '14px 8px 0',
                    }}>
                        <TIcon name="brand-google"/>
                        Đang đăng ký với {hoSoGoogle?.email}
                    </p> : <></>}

                    <FieldInput label="TÊN" name="name" icon="user" placeholder="Nguyễn Hoàng Phong" required
                                value={formData.name} error={formError.name} onChange={changeInput}/>
                    <FieldInput label="EMAIL" name="email" type="email" icon="mail" placeholder="ban@email.com"
                                required value={formData.email} error={formError.email}
                                disabled={laGoogle}
                                hint={laGoogle ? 'Email do Google cung cấp, không sửa được.' : undefined}
                                onChange={changeInput}/>
                    <FieldInput label="SỐ ĐIỆN THOẠI" name="username" icon="phone" placeholder="0901234567" required
                                value={formData.username} error={formError.username}
                                hint="Số này cũng là tên đăng nhập của bạn."
                                onChange={changeInput}/>
                    {laGoogle ? <></> : <>
                        <FieldInput label="MẬT KHẨU" name="password" type="password" icon="lock" placeholder="••••••••"
                                    required value={formData.password} error={formError.password}
                                    onChange={changeInput}/>
                        <FieldInput label="XÁC NHẬN MẬT KHẨU" name="confirm_password" type="password" icon="lock"
                                    placeholder="••••••••" required value={formData.confirm_password}
                                    error={formError.confirm_password} onChange={changeInput}/>
                    </>}
                </>
                : <></>}

            {buoc === 'anh' ? <>
                {base64FileUrl ? <div style={{display: 'flex', justifyContent: 'center'}}>
                    <Cropper
                        src={base64FileUrl.toString()}
                        style={{height: 150, width: 150}}
                        initialAspectRatio={1}
                        guides={true}
                        ref={cropperRef}
                        cropBoxResizable={false}
                        scalable={false}
                        cropBoxMovable={false}
                        dragMode={'move'}
                    />
                </div> : ''}

                <label style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 14, padding: '10px 0 6px', cursor: 'pointer',
                }}>
                    {base64FileUrl ? <div/> : <span className="avatar" style={{
                        width: 110, height: 110, fontSize: 30,
                    }}>
                        {/* Chưa chọn ảnh mà đăng ký bằng Google thì đã có ảnh Google
                            để dùng làm mặc định — cho họ thấy trước thứ sẽ được lưu. */}
                        {laGoogle && hoSoGoogle?.picture
                            ? <img src={hoSoGoogle.picture} alt=""/>
                            : <TIcon name="camera"/>}
                    </span>}
                    {/* Đang có sẵn ảnh (ảnh Google, hoặc ảnh vừa chọn) thì việc
                        người dùng sắp làm là ĐỔI, không phải thêm. */}
                    <span style={{color: 'var(--biadi-red)', font: '600 12px var(--font-ui)'}}>
                        {dangCoAnh() ? 'Đổi ảnh đại diện' : '+ Thêm ảnh đại diện'}
                    </span>
                    <input className="hidden" type="file" name="avatar" accept="image/*"
                           style={{display: 'none'}}
                           onChange={(event) => handleFileInputChange(event)}/>
                </label>

                {laGoogle && hoSoGoogle?.picture && !base64FileUrl ? <p style={{
                    textAlign: 'center', color: 'var(--biadi-muted)', font: '500 12px var(--font-ui)',
                }}>
                    Bỏ qua để dùng ảnh đại diện Google của bạn.
                </p> : <></>}
            </> : <></>}

            {buoc === 'otp' ? <>
                <p style={{color: 'var(--biadi-steel)', font: '500 13px var(--font-ui)', padding: '14px 8px 0'}}>
                    Mã xác nhận đã được gửi tới <strong>{formData.email.trim()}</strong>.
                    Nhập mã để tiếp tục — tài khoản chỉ được tạo sau bước này.
                </p>
                <FieldInput label="MÃ XÁC NHẬN" name="otp" type="number" icon="shield-lock" placeholder="123456"
                            required autoComplete="one-time-code"
                            value={formData.otp} error={formError.otp}
                            hint="Mã có hiệu lực 15 phút. Không thấy thư thì xem cả mục Spam."
                            onChange={changeInput}/>
                <div className="form-meta">
                    {/* Sai email thì phải quay lại sửa, không thì có gửi lại bao
                        nhiêu lần cũng vào hộp thư của người khác. */}
                    <button type="button" className="remember" onClick={() => setStep(step - 1)}>
                        Sửa email
                    </button>
                    {choGuiLai > 0
                        ? <span style={{color: 'var(--biadi-muted)', font: '500 12px var(--font-ui)'}}>
                            Gửi lại mã sau {choGuiLai}s
                        </span>
                        : <button type="button" style={{color: 'var(--biadi-red)', font: '600 12px var(--font-ui)'}}
                                  onClick={() => void guiLaiMa()}>
                            Gửi lại mã
                        </button>}
                </div>
            </> : <></>}

            {errorMessage ? <span className="field-error" style={{whiteSpace: 'pre-line'}}>
                <TIcon name="alert-circle"/>{errorMessage}
            </span> : <></>}

            <button type="submit" className="btn btn-primary" style={{marginTop: 4}} disabled={loader}>
                {buoc === 'anh' ? 'XÁC NHẬN' : 'TIẾP TỤC'}
            </button>
        </form>

        {/* Đặt dưới nút ĐĂNG KÝ, giống màn đăng nhập. Chỉ ở bước 1 và chỉ khi
            chưa đi theo luồng Google - đang ở giữa luồng Google thì bấm lại nút
            này chẳng thay đổi gì. */}
        {buoc === 'thongTin' && !laGoogle ? <>
            <div className="auth-divider">
                <span className="rule"/>
                <span className="text">hoặc đăng ký với</span>
                <span className="rule"/>
            </div>

            {/* Chỉ Google - xem ghi chú cùng chỗ này ở màn đăng nhập. */}
            <div className="socials-auth">
                <button type="button" className="social-btn" onClick={() => void bamGoogle()}>
                    <TIcon name="brand-google"/>
                    Google
                </button>
            </div>
        </> : <></>}
    </AuthShell>;
};

export default RegisterPage;
